import type { Result } from '@api';
import { Success, Failure, useErrorMessage, useDatahubAPI } from '@api';
import { projectAtom } from '@data/auth';
import type { DailyMetrics, Metrics } from '@data/datahub/metrics/types';
import { timezoneAtom } from '@data/datahub/timezone/states';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

type APIResponse = {
  total_metrics: Metrics;
};

export const useMetricsAPI = (): {
  loading: boolean;
  getMetrics: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<Metrics | null>;
} => {
  const [loading, setLoading] = useState(false);
  const { notifyError } = useNotification();
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const { datahubAPI } = useDatahubAPI();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      params: URLSearchParams,
    ): Promise<Result<APIResponse, AxiosResponse>> => {
      try {
        const res = await datahubAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/metrics${
            params ? `?${params.toString()}` : ''
          }`,
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [datahubAPI],
  );

  const getMetrics = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, params: URLSearchParams) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        const timezone = get(timezoneAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.datahub.get_metrics', { status: 'failed' }),
          });
          setLoading(false);
          return null;
        }
        setLoading(true);

        // タイムゾーンをセット
        params.append('timezone', timezone.timezone);

        const res = await request(
          project.id,
          environment.environment_id,
          vehicleId,
          params,
        );
        if (!res.value || res.isFailure()) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((res as any).value?.status !== 404) {
            notifyError({
              message: `${t('api.datahub.get_metrics', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
          }
          setLoading(false);
          return null;
        }
        setLoading(false);
        return res.value.total_metrics;
      },
      [notifyError, t, request, getErrorMessage],
    ),
  );

  return {
    loading,
    getMetrics,
  };
};

type DailyMetricsAPIResponse = {
  daily_metrics: DailyMetrics[];
  next_page_token: string;
};

export const useDailyMetricsAPI = () => {
  const [loading, setLoading] = useState(false);
  const { notifyError } = useNotification();
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const { datahubAPI } = useDatahubAPI();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      params: URLSearchParams,
    ): Promise<Result<DailyMetricsAPIResponse, AxiosResponse>> => {
      try {
        const res = await datahubAPI.get(
          `/${projectId}/environments/${environmentId}/${
            vehicleId === 'all' ? '' : `vehicles/${vehicleId}/`
          }metrics/daily?${params.toString()}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [datahubAPI],
  );

  const getDailyMetrics = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, params: URLSearchParams) => {
        setLoading(true);
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.datahub.get_metrics_daily', { status: 'failed' }),
          });
          setLoading(false);
          return [];
        }

        const req = async (requestParams: URLSearchParams) =>
          await request(
            project.id,
            environment.environment_id,
            vehicleId,
            requestParams,
          );

        const splitRequest = async (
          datas: DailyMetrics[] = [],
        ): Promise<DailyMetrics[]> => {
          const res = await req(params);
          // 取得失敗時
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t('api.datahub.get_metrics_daily', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
            return datas;
          }
          // 取得成功時
          if (!!res && res.isSuccess()) {
            // 配列を結合
            const combinedDatas = [...datas, ...res.value.daily_metrics];
            if (res.value.next_page_token) {
              // レスポンスに next_page_token がある場合は次のリクエストを行う
              if (params) {
                params.set('page_token', res.value.next_page_token);
              }
              await splitRequest(combinedDatas);
            } else {
              return combinedDatas;
            }
          }
          return datas;
        };

        const datas = await splitRequest();
        setLoading(false);
        return datas;
      },
      [notifyError, request, t, getErrorMessage],
    ),
  );

  return {
    loading,
    getDailyMetrics,
  };
};
